import { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader, StatusPill, Btn, Input } from "@/components/ui-kit";
import { formatDate } from "@/lib/format";
import { Search, MoreVertical, ShieldOff, ShieldCheck } from "lucide-react";

export default function AdminTenants() {
  const [tenants, setTenants] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");

  const load = async () => {
    const params = {};
    if (q) params.search = q;
    if (status) params.status = status;
    const { data } = await api.get("/admin/tenants", { params });
    setTenants(data);
  };

  useEffect(() => { load(); }, [status]);

  const act = async (id, action) => {
    await api.post(`/admin/tenants/${id}/${action}`);
    load();
  };

  return (
    <div data-testid="admin-tenants-page">
      <PageHeader
        title="Tenants"
        subtitle="All cab operators using WebCabz. Activate, suspend or delete accounts."
      />
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-zinc-400" />
          <Input
            data-testid="tenant-search"
            placeholder="Search by company, email or owner…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load()}
            className="!pl-10"
          />
        </div>
        <select
          data-testid="tenant-status-filter"
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          className="px-3 py-2 rounded-md border border-zinc-300 bg-white text-sm"
        >
          <option value="">All statuses</option>
          <option value="active">Active</option>
          <option value="trial">Trial</option>
          <option value="suspended">Suspended</option>
        </select>
        <Btn onClick={load}>Apply</Btn>
      </div>

      <div className="bg-white border border-zinc-200 rounded-lg overflow-hidden">
        <table className="tbl">
          <thead>
            <tr>
              <th>Company</th><th>Owner</th><th>Plan</th><th>Status</th><th>Expiry</th><th>Created</th><th className="text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {tenants.length === 0 ? (
              <tr><td colSpan={7} className="text-center py-10 text-zinc-500">No tenants found.</td></tr>
            ) : tenants.map((t) => (
              <tr key={t.id} data-testid={`tenant-row-${t.id}`}>
                <td>
                  <div className="font-medium text-asphalt">{t.company_name}</div>
                  <div className="text-xs text-zinc-500">{t.email}</div>
                </td>
                <td>{t.owner_name}<div className="text-xs text-zinc-500">{t.mobile || "—"}</div></td>
                <td>{t.plan_name || "—"}</td>
                <td><StatusPill status={t.status} /></td>
                <td className="text-zinc-600">{formatDate(t.expiry_date)}</td>
                <td className="text-zinc-500">{formatDate(t.created_at)}</td>
                <td className="text-right">
                  {t.status === "suspended" ? (
                    <Btn variant="outline" data-testid={`tenant-activate-${t.id}`} onClick={() => act(t.id, "activate")}>
                      <ShieldCheck className="h-4 w-4" /> Activate
                    </Btn>
                  ) : (
                    <Btn variant="outline" data-testid={`tenant-suspend-${t.id}`} onClick={() => act(t.id, "suspend")}>
                      <ShieldOff className="h-4 w-4" /> Suspend
                    </Btn>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
